// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Ressourcer's Galleri (`?view=galleri`) as data: a card's one-line
 * description, its miljøstatus pill, the placeholder for a card without a
 * resource image, and how the arrow keys move through the grid. Which types
 * or parts the grid shows is the shared filtered set (`resourceFilter`). The
 * image itself is `sys:view` (`kortlaegning/view` `shownView`) — there is no
 * ranked best-frame fallback here either. Pure: no React, no DOM.
 */

import type { Buildup, ResourceKey, Sample, SurveyPart, SurveyType } from '../api/types';
import type { ResourceIndex } from '../kortlaegning/resources';
import { ENV_LABEL, formatTonnes } from '../kortlaegning/vocab';
import { buildupColumnKey, currentBuildup } from './buildups';
import { typeValue } from './fields';
import { MATERIAL_LABEL, materialOf, materialToken } from './lens';
import { isManualType, isScanPart } from './provenance';
import { massText, type PartsFigure } from './resourceFilter';
import { envBadge, type Badge } from './review';

/** The toolbar count for Dele: `N dele`, and ` · M fejldetekteret` when any are. */
export function partCountText(live: number, dismissed: number): string {
  const n = live === 1 ? '1 del' : `${live} dele`;
  return dismissed > 0 ? `${n} · ${dismissed} fejldetekteret` : n;
}

/**
 * A part card's image well without a resource image: tinted by its type's
 * material, "Uden geometri" for a hand-made part, "Intet billede" otherwise
 * (as `galleryPlaceholder` says for a type).
 */
export function galleryPartPlaceholder(
  part: Pick<SurveyPart, 'instance_guid' | 'instance_id'>,
  type: Pick<SurveyType, 'name' | 'eak_code' | 'parts'>,
): { token: string; text: string } {
  return { token: materialToken(materialOf(type)), text: isScanPart(part) ? 'Intet billede' : 'Uden geometri' };
}

/**
 * A card's muted line: `<primary> · <N dele> · <masse>`. The primary is the
 * type's build-up (its `buildup_id`, or the `Opbygning` column on an older
 * server), else its EAK name, else its material family. With `figure`
 * (`roomFigure`), the parts and tonnes are the room focus's share.
 */
export function galleryDescription(
  type: SurveyType,
  catalogue: readonly ResourceKey[],
  library: readonly Buildup[],
  index: ResourceIndex,
  figure?: PartsFigure,
): string {
  const key = buildupColumnKey(catalogue);
  const columnValue = key ? typeValue(type, key, index).value : null;
  const eak = type.eak_name.trim();
  const primary = currentBuildup(type, library, columnValue)?.name ?? (eak !== '' ? eak : MATERIAL_LABEL[materialOf(type)]);
  const n = figure ? figure.parts : type.parts.length;
  const parts = n === 1 ? '1 del' : `${n} dele`;
  let mass: string;
  if (figure) mass = figure.mass === null ? 'tons mangler' : massText(figure);
  else mass = type.mass_t === null ? 'tons mangler' : formatTonnes(type.mass_t);
  return `${primary} · ${parts} · ${mass}`;
}

/** The miljøstatus pill: `envBadge`, and "Ren" for a type only screened (the card always says something). */
export function galleryEnvPill(type: SurveyType, samples: readonly Sample[]): Badge {
  return envBadge(type, samples) ?? { label: ENV_LABEL.ren_screening, tone: 'good' };
}

/**
 * The image well without a resource image: tinted by the type's material
 * (a token name), with "Uden geometri" for a hand-made type (nothing to
 * photograph in the scan) and "Intet billede" otherwise.
 */
export function galleryPlaceholder(type: Pick<SurveyType, 'name' | 'eak_code' | 'parts'>): { token: string; text: string } {
  return { token: materialToken(materialOf(type)), text: isManualType(type) ? 'Uden geometri' : 'Intet billede' };
}

/**
 * The card index after an arrow key in a grid of `count` cards and `columns`
 * columns: ←/→ one step, ↑/↓ one row, Home/End the ends — clamped to the
 * cards. ↑ in the first row stays; ↓ into a shorter last row lands on its
 * last card, ↓ in the last row stays. Null for any other key (or no cards),
 * so the caller leaves it alone.
 */
export function gridMove(index: number, key: string, count: number, columns: number): number | null {
  if (count <= 0) return null;
  const cols = Math.max(1, Math.floor(columns));
  const at = Math.min(count - 1, Math.max(0, index));
  switch (key) {
    case 'ArrowLeft':
      return Math.max(0, at - 1);
    case 'ArrowRight':
      return Math.min(count - 1, at + 1);
    case 'ArrowUp':
      return at - cols >= 0 ? at - cols : at;
    case 'ArrowDown': {
      const lastRow = Math.floor((count - 1) / cols);
      return Math.floor(at / cols) < lastRow ? Math.min(count - 1, at + cols) : at;
    }
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
